import React, { useEffect, useMemo, useRef, useState } from "react";
import Input from "../../../../../../components/Elements/Input/Input";
// import UploadCloudSvg from "../../../../../../assets/svgs/Upload cloud.svg";
import EyeSvg from "../../../../../../assets/svgs/eye.svg";
import { getProfileImageUrl } from "../profileFormUtils";
import { Upload, Eye } from "lucide-react";

const MAX_FILE_SIZE_MB = 5;
const ALLOWED_TYPES = [
  "application/pdf",
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/webp",
];

// Mirrors the backend regexes in customerProfileUpdate so the person sees
// the same validation error before they ever hit Save.
const REG_VALIDATORS = {
  CIN: {
    regex: /^[A-Z]{1}[0-9]{5}[A-Z]{2}[0-9]{4}[A-Z]{3}[0-9]{6}$/i,
    message: "Invalid CIN format. Expected e.g. U12345MH2010PTC123456.",
  },
  PAN: {
    regex: /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/i,
    message: "Invalid PAN format. Expected e.g. ABCDE1234F.",
  },
  DUNS: {
    regex: /^\d{9}$/,
    message: "DUNS must be exactly 9 digits.",
  },
  VAT: {
    regex: /^[A-Z0-9]{4,20}$/i,
    message: "VAT must be 4–20 alphanumeric characters.",
  },
};

const validateFile = (file) => {
  if (!file) return null;
  if (!ALLOWED_TYPES.includes(file.type)) {
    return "Only PDF, PNG, JPG or WEBP files are allowed.";
  }
  if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
    return `File must be smaller than ${MAX_FILE_SIZE_MB}MB.`;
  }
  return null;
};

// One CIN / VAT / PAN / DUNS field. Owns its own local preview URL (so the
// View icon works the instant a file is picked, before it's ever saved) and
// its own validation-error state, rendered directly under the field.
const DocumentField = ({ docKey, label, doc, onChange }) => {
  const fileInputRef = useRef(null);
  const [fileError, setFileError] = useState(null);
  const [numberError, setNumberError] = useState(null);

  const localPreviewUrl = useMemo(() => {
    if (!doc.file) return null;
    return URL.createObjectURL(doc.file);
  }, [doc.file]);

  useEffect(() => {
    return () => {
      if (localPreviewUrl) URL.revokeObjectURL(localPreviewUrl);
    };
  }, [localPreviewUrl]);

  const serverUrl = getProfileImageUrl(doc.filePath);
  const viewUrl = localPreviewUrl || serverUrl;
  const hasDocument = Boolean(doc.file || doc.filePath);

  const handleNumberChange = (e) => {
    const val = e.target.value;
    onChange(docKey, "number", val);

    const validator = REG_VALIDATORS[docKey];
    if (validator && val.trim() !== "" && !validator.regex.test(val.trim())) {
      setNumberError(validator.message);
    } else {
      setNumberError(null);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file later
    if (!file) return;

    const validationError = validateFile(file);
    if (validationError) {
      setFileError(validationError);
      return;
    }

    setFileError(null);
    onChange(docKey, "file", file);
  };

  return (
    <div>
      <label className="block text-gray-500 mb-1">{label}</label>

      <div className="flex flex-wrap items-center gap-3">
        <Input
          type="text"
          placeholder={`Enter ${label}`}
          value={doc.number || ""}
          onChange={handleNumberChange}
          className="w-60"
          variant="secondary"
        />

        <input
          ref={fileInputRef}
          type="file"
          accept={ALLOWED_TYPES.join(",")}
          className="hidden"
          onChange={handleFileSelect}
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="text-[#06513D] hover:text-[#387464] cursor-pointer"
        >
          <Upload size={20} />
        </button>

        {hasDocument && (
          <button
            type="button"
            onClick={() =>
              window.open(viewUrl, "_blank", "noopener,noreferrer")
            }
            className="text-[#06513D] hover:text-[#387464] cursor-pointer"
          >
            <Eye size={20} />
          </button>
        )}

        {doc.file ? (
          <span
            className="text-xs text-gray-600 truncate max-w-[140px]"
            title={doc.file.name}
          >
            {doc.file.name}
          </span>
        ) : doc.filePath ? (
          <span
            className="text-xs text-emerald-700 font-medium truncate max-w-[140px]"
            title={doc.filePath.split("/").pop()}
          >
            {doc.filePath.split("/").pop() || "Uploaded"}
          </span>
        ) : null}

        {!hasDocument && (
          <span className="text-red-500 text-xs">No document uploaded</span>
        )}
      </div>

      {numberError && (
        <p className="text-red-500 text-xs mt-1">{numberError}</p>
      )}
      {fileError && <p className="text-red-500 text-xs mt-1">{fileError}</p>}
    </div>
  );
};

const OrganizationDocumentsUpload = ({
  uploadDocuments,
  handleDocumentChange,
}) => {
  return (
    <div className="pt-2">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {["CIN", "VAT", "PAN", "DUNS"].map((key) => (
          <DocumentField
            key={key}
            docKey={key}
            label={key}
            doc={uploadDocuments[key] || {}}
            onChange={handleDocumentChange}
          />
        ))}
      </div>
    </div>
  );
};

export default OrganizationDocumentsUpload;
